import { useOptionEntityOptionSource } from '@app/main/utils';
import {
  FormHostSearch,
  AppointmentSlotApplyForm,
  AppointmentSlotApplyFormContent,
  AppointmentSlotApplyFormValue,
} from '@mod/schedule';
import { Form } from '@webinex/antik';
import { Radio, Select, Typography } from 'antd';
import { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { TextBox } from '@app/ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';

type HostSearchBy = 'PROVIDER' | 'SPECIALTY';
type HostSearchContextValue = { searchBy: HostSearchBy; specialtyId: string | null };

type HostSearchContext = {
  value: HostSearchContextValue;
  setValue: (update: (prev: HostSearchContextValue) => HostSearchContextValue) => void;
};

const HostSearchContext = React.createContext<HostSearchContext>(undefined!);

function HostSearchProvider({ children }: PropsWithChildren<object>) {
  const [value, setValue] = useState<HostSearchContextValue>({ searchBy: 'PROVIDER', specialtyId: null });
  const contextValue = useMemo(
    () => ({ value, setValue: setValue } satisfies HostSearchContext),
    [value, setValue],
  );
  return <HostSearchContext.Provider value={contextValue}>{children}</HostSearchContext.Provider>;
}

export const CustomAppointmentSlotApplyForm: React.ComponentType<
  React.ComponentProps<typeof AppointmentSlotApplyForm>
> = (props) => {
  return (
    <HostSearchProvider>
      <AppointmentSlotApplyForm.Component {...props} />
    </HostSearchProvider>
  );
};

function FormHostSearchBy() {
  const { value, setValue } = useContext(HostSearchContext);
  const { t } = useTranslation(undefined, { keyPrefix: 'appointments.apply' });

  return (
    <TextBox label={t('searchBy')} className="mb-6">
      <Radio.Group
        value={value.searchBy}
        onChange={(e) => setValue((prev) => ({ ...prev, searchBy: e.target.value }))}
        options={[
          { value: 'PROVIDER' satisfies HostSearchBy, label: t('PROVIDER') },
          { value: 'SPECIALTY' satisfies HostSearchBy, label: t('SPECIALTY') },
        ]}
      />
    </TextBox>
  );
}

export const CustomFormHostSearch: React.ComponentType<React.ComponentProps<typeof FormHostSearch>> = (
  props,
) => {
  const { value } = useContext(HostSearchContext);
  const extra = value.searchBy === 'SPECIALTY' ? { specialtyId: value.specialtyId } : {};
  return (
    <FormHostSearch.Component
      {...props}
      extra={extra}
      skip={value.searchBy === 'SPECIALTY' && !value.specialtyId}
    />
  );
};

export function FormSpecialtySearch() {
  const specialtyOptionSource = useOptionEntityOptionSource({ discriminator: 'specialty' });
  const [select] = Form.useAsyncSelect({ optionSource: specialtyOptionSource });
  const { value, setValue } = useContext(HostSearchContext) ?? {};

  return (
    <TextBox label="Specialty" className="mb-6">
      <Select
        {...select}
        value={value.specialtyId}
        onSelect={(x) => setValue((prev) => ({ ...prev, specialtyId: x }))}
        className="w-full"
      />
    </TextBox>
  );
}

export const CustomAppointmentSlotApplyFormContent: (typeof AppointmentSlotApplyFormContent)['Component'] = (
  props,
) => {
  const { value } = useContext(HostSearchContext);
  const { t } = useTranslation(undefined, { keyPrefix: 'appointments.apply' });
  const { setFieldValue } = useFormikContext<AppointmentSlotApplyFormValue>();

  useEffect(() => {
    setFieldValue('hostId', null);
  }, [value.searchBy, value.specialtyId]);

  return (
    <AppointmentSlotApplyFormContent.Component
      {...props}
      onlyHostMatchSlots={value.searchBy === 'SPECIALTY'}
      render={({ formAppointmentTypeItem, formHostSearch, slotCalendar, formSlotPicker, addBlurb }) => (
        <>
          {formAppointmentTypeItem}
          <FormHostSearchBy />
          {value.searchBy === 'PROVIDER' && formHostSearch}
          {value.searchBy === 'SPECIALTY' && <FormSpecialtySearch />}
          <Typography.Paragraph type="secondary">{t('hint_' + value.searchBy)}</Typography.Paragraph>
          {slotCalendar}
          {value.searchBy === 'SPECIALTY' && formHostSearch}
          {formSlotPicker}
          {addBlurb}
        </>
      )}
      extraSlotQueryArg={
        value.specialtyId && value.searchBy === 'SPECIALTY'
          ? { specialtyIds: [value.specialtyId] }
          : undefined
      }
    />
  );
};
